import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import adminApiService from '../../services/adminApiService';
import toast from 'react-hot-toast';
import {
  UsersIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  UserPlusIcon,
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  KeyIcon
} from '@heroicons/react/24/outline';
import { formatDate, formatDateTime, debugDate } from '../../utils/dateUtils';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: 'student' | 'employer';
  verified?: boolean;
  suspended?: boolean;
  createdAt: any; // Firestore Timestamp
  updatedAt?: any;
  lastLogin?: any;
  companyName?: string;
  identityVerification?: {
    status: 'pending' | 'verified' | 'rejected';
  };
  profile?: {
    avatar?: string;
    company?: string;
    skills?: string[];
    location?: string;
  };
}

interface PaginatedUsers {
  users: AdminUser[];
  total: number;
  totalPages: number;
  currentPage: number;
  summary: {
    totalUsers: number;
    totalStudents: number;
    totalEmployers: number;
    verifiedUsers: number;
    pendingVerification: number;
  };
}

const UserManagement: React.FC = () => {
  const { hasPermission } = useAdmin();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    userType: 'all',
    verified: 'all',
    suspended: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [summary, setSummary] = useState({
    totalUsers: 0,
    totalStudents: 0,
    totalEmployers: 0,
    verifiedUsers: 0,
    pendingVerification: 0
  });
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    userType: 'student' as 'student' | 'employer',
    companyName: '',
    suspended: false
  });

  useEffect(() => {
    fetchUsers();
  }, [currentPage, filters, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit: 20,
        search: searchTerm || undefined
      };

      if (filters.userType !== 'all') {
        params.userType = filters.userType;
      }
      if (filters.verified !== 'all') {
        params.verified = filters.verified === 'verified';
      }
      if (filters.suspended !== 'all') {
        params.suspended = filters.suspended === 'suspended';
      }

      const response = await adminApiService.getAllUsers(params);
      const data = response.data || response; // Handle different response structures
      
      setUsers(data.users || []);
      setTotalPages(data.totalPages || 1);
      setTotalUsers(data.total || 0);
      setSummary(data.summary || {
        totalUsers: 0,
        totalStudents: 0,
        totalEmployers: 0,
        verifiedUsers: 0,
        pendingVerification: 0
      });
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async (userId: string, reason: string) => {
    try {
      await adminApiService.suspendUser(userId, reason);
      toast.success('User suspended successfully');
      fetchUsers();
    } catch (error: any) {
      toast.error('Failed to suspend user');
    }
  };

  const handleUnsuspendUser = async (userId: string) => {
    try {
      await adminApiService.unsuspendUser(userId);
      toast.success('User unsuspended successfully');
      fetchUsers();
    } catch (error: any) {
      toast.error('Failed to unsuspend user');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await adminApiService.deleteUser(userId);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error: any) {
        toast.error('Failed to delete user');
      }
    }
  };

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    setEditFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      userType: user.userType || 'student',
      companyName: user.companyName || '',
      suspended: user.suspended || false
    });
    setEditMode(true);
    setShowUserModal(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await adminApiService.updateUser(selectedUser.id, editFormData);
      toast.success('User updated successfully');
      setShowUserModal(false);
      setEditMode(false);
      fetchUsers();
    } catch (error: any) {
      toast.error('Failed to update user');
    }
  };

  const handleCloseModal = () => {
    setShowUserModal(false);
    setEditMode(false);
    setSelectedUser(null);
  };

  const handleResetPassword = async (userId: string, userEmail: string) => {
    if (window.confirm(`Are you sure you want to send a password reset email to ${userEmail}?`)) {
      try {
        // We'll use the existing forgot password endpoint
        await adminApiService.resetUserPassword(userId);
        toast.success('Password reset email sent successfully');
      } catch (error: any) {
        toast.error('Failed to send password reset email');
      }
    }
  };

  const UserModal: React.FC<{ user: AdminUser; onClose: () => void }> = ({ user, onClose }) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {editMode ? 'Edit User' : 'User Details'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <span className="sr-only">Close</span>
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">First Name</label>
              {editMode ? (
                <input
                  type="text"
                  value={editFormData.firstName}
                  onChange={(e) => setEditFormData({...editFormData, firstName: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{user.firstName || 'N/A'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Name</label>
              {editMode ? (
                <input
                  type="text"
                  value={editFormData.lastName}
                  onChange={(e) => setEditFormData({...editFormData, lastName: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{user.lastName || 'N/A'}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              {editMode ? (
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{user.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">User Type</label>
              {editMode ? (
                <select
                  value={editFormData.userType}
                  onChange={(e) => setEditFormData({...editFormData, userType: e.target.value as 'student' | 'employer'})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                >
                  <option value="student">Student</option>
                  <option value="employer">Employer</option>
                </select>
              ) : (
                <p className="mt-1 text-sm text-gray-900 capitalize">{user.userType}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <div className="mt-1 flex items-center space-x-2">
                {user.verified && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                    Verified
                  </span>
                )}
                {user.suspended && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <NoSymbolIcon className="w-3 h-3 mr-1" />
                    Suspended
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Company Name for Employers */}
          {(editMode && editFormData.userType === 'employer') || (!editMode && user.userType === 'employer') ? (
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Name</label>
              {editMode ? (
                <input
                  type="text"
                  value={editFormData.companyName}
                  onChange={(e) => setEditFormData({...editFormData, companyName: e.target.value})}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              ) : (
                <p className="mt-1 text-sm text-gray-900">{user.companyName || 'N/A'}</p>
              )}
            </div>
          ) : null}

          {/* Suspended Control in Edit Mode */}
          {editMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Account Status</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={editFormData.suspended}
                    onChange={(e) => setEditFormData({...editFormData, suspended: e.target.checked})}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Suspend user account</span>
                </label>
              </div>
            </div>
          )}

          {!editMode && user.profile && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Profile Information</label>
              <div className="bg-gray-50 p-3 rounded-md">
                {user.profile.company && (
                  <p className="text-sm"><strong>Company:</strong> {user.profile.company}</p>
                )}
                {user.profile.location && (
                  <p className="text-sm"><strong>Location:</strong> {user.profile.location}</p>
                )}
                {user.profile.skills && (
                  <div className="mt-2">
                    <strong className="text-sm">Skills:</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {user.profile.skills.map((skill, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Created At</label>
                              <p className="mt-1 text-sm text-gray-900">
                  {formatDate(user.createdAt)}
                </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Last Login</label>
              <p className="mt-1 text-sm text-gray-900">
                {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          >
            Cancel
          </button>
          {editMode ? (
            <button
              onClick={handleUpdateUser}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Save Changes
            </button>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Edit User
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600">Manage platform users and their permissions</p>
          </div>
          {hasPermission('users', 'create') && (
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="flex items-center">
            <UsersIcon className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{(totalUsers || 0).toLocaleString()}</p>
            </div>
          </div>
        </div>
        {/* Add more stat cards as needed */}
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          {/* Search */}
          <div className="relative max-w-md">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Filters */}
          <div className="flex space-x-4">
            <select
              value={filters.userType}
              onChange={(e) => setFilters({ ...filters, userType: e.target.value })}
              className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Types</option>
              <option value="student">Students</option>
              <option value="employer">Employers</option>
            </select>

            <select
              value={filters.verified}
              onChange={(e) => setFilters({ ...filters, verified: e.target.value })}
              className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="unverified">Unverified</option>
            </select>

            <select
              value={filters.suspended}
              onChange={(e) => setFilters({ ...filters, suspended: e.target.value })}
              className="border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white shadow border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-gray-700">
                            {(user.firstName || user.lastName || user.email || 'U').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Name'}</div>
                          <div className="text-sm text-gray-500">{user.email || 'No Email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.userType === 'student' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user.userType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {user.verified && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Verified
                          </span>
                        )}
                        {user.suspended && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Suspended
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setEditMode(false);
                            setShowUserModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit user"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => handleResetPassword(user.id, user.email)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Send password reset email"
                        >
                          <KeyIcon className="h-4 w-4" />
                        </button>
                        
                        {hasPermission('users', 'update') && (
                          <>
                            {user.suspended ? (
                              <button
                                onClick={() => handleUnsuspendUser(user.id)}
                                className="text-green-600 hover:text-green-900"
                                title="Unsuspend user"
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleSuspendUser(user.id, 'Admin action')}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Suspend user"
                              >
                                <NoSymbolIcon className="h-4 w-4" />
                              </button>
                            )}
                          </>
                        )}

                        {hasPermission('users', 'delete') && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete user"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <UserModal
          user={selectedUser}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default UserManagement; 